/*
次のレッスンへ移るときの幕。押した場所から緑の円が広がって画面を覆い、
次の回が描き終わったら同じ場所へ縮んで消える。

# なぜ要るか

押した瞬間から次の回が出るまで、画面は何も言わない。読み込みが速ければ
中身だけが瞬間で入れ替わり、遅ければ止まって見える。**どちらも「進んだ」
という感じがしない。** 幕を1枚かけると、待ちが待ちに見えるし、切り替えも
段差でなく動きになる。

# 明けるタイミングは React の transition で取る

`run()` は `useTransition` の中で走らせる。**React が commit して
1フレーム描くまで明けるのを待つ。** 自前で書いていたときは
react-router の navigation.state が idle に戻ったのを合図にしていたが、
これは「loader が終わった」であって「新しい画面が描けた」ではない。
isPending が折れたあと1フレーム待ってから明ける。

幕は押した座標を原点に `clip-path: circle()` で広げる虹彩。
色は `.motion-curtain` に当てる (app.css)。

# 押した場所をどう知るか

props で渡すと雪だるま式に増える。次へ進む道は下のボタンだけでなく、
スナックバーの導線やモーダルを閉じたあとにもあり、そこにはクリックの
座標が無い。**最後に触った場所を1か所で覚えておく。** 触っていなければ
画面の下の中央から開く (下のボタンがある場所)。
*/
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useTransition,
  type ReactNode,
} from "react";

type Advance = (run: () => void) => void;

const LessonCurtainContext = createContext<Advance | null>(null);

/** 次へ進む操作を幕でくるむ。文脈が無い画面では素通しで実行する */
export function useLessonCurtain(): Advance {
  const advance = useContext(LessonCurtainContext);
  return advance ?? ((run: () => void) => run());
}

/* 覆いは短く、明けは少し長く。閉じるのは待たせる時間、
   明けるのは新しい画面を見せる時間なので、役目が違う */
const COVER_MS = 340;
const REVEAL_MS = 460;
/* commit が異常に遅い画面でも幕が張りっぱなしにならない上限 */
const COMMIT_TIMEOUT_MS = 3000;

type Curtain = { originX: number; originY: number };

export function LessonCurtainProvider({ children }: { children: ReactNode }) {
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const [curtain, setCurtain] = useState<Curtain | null>(null);
  const [phase, setPhase] = useState<"cover" | "reveal">("cover");
  /* run() を transition に渡し終えた印。ref だと effect の発火条件に
     ならないので state にする */
  const [covered, setCovered] = useState(false);
  const [pending, startTransition] = useTransition();
  const timers = useRef<number[]>([]);
  /* 前の遷移の fallback タイマーが次の幕を明けないようにする世代番号 */
  const generation = useRef(0);

  /* 最後に触った場所を覚える。capture で拾うのは、押した先が
     stopPropagation していても座標だけは要るため */
  useEffect(() => {
    const remember = (e: PointerEvent) => {
      pointerRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("pointerdown", remember, { capture: true });
    return () =>
      window.removeEventListener("pointerdown", remember, { capture: true });
  }, []);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const later = useCallback((ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);

  /* run() を渡し終え、commit が済んだら (pending が折れたら)
     1フレーム置いて明ける。run() が同期で終わる画面では
     pending が立たないまま covered が立つので、その変化でここに来る */
  useEffect(() => {
    if (!curtain || phase !== "cover" || !covered || pending) return;
    requestAnimationFrame(() => setPhase("reveal"));
  }, [curtain, phase, covered, pending]);

  const advance = useCallback<Advance>(
    (run) => {
      if (typeof window === "undefined") {
        run();
        return;
      }
      if (
        window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
        curtain !== null
      ) {
        /* 幕の最中の再入は透過クリックではなく呼び出し側からも来得る。
           1枚の幕で1回だけ進める */
        if (curtain === null) run();
        return;
      }
      const point = pointerRef.current ?? {
        x: window.innerWidth / 2,
        y: window.innerHeight - 48,
      };
      try {
        const gen = ++generation.current;
        setCovered(false);
        setPhase("cover");
        setCurtain({ originX: point.x, originY: point.y });
        later(COVER_MS, () => {
          /* 覆いきった直後に進める。commit まで待つのは上の effect */
          startTransition(() => run());
          setCovered(true);
          later(COMMIT_TIMEOUT_MS, () => {
            if (generation.current === gen) setPhase("reveal");
          });
        });
      } catch {
        /* 幕が張れなくても必ず進める。**演出の失敗で導線を殺さない。** */
        run();
      }
    },
    [later, curtain],
  );

  const handleTransitionEnd = useCallback(() => {
    if (phase === "reveal") {
      setCurtain(null);
      setCovered(false);
    }
  }, [phase]);

  return (
    <LessonCurtainContext.Provider value={advance}>
      {children}
      {curtain && (
        <CurtainOverlay
          curtain={curtain}
          phase={phase}
          onEnd={handleTransitionEnd}
        />
      )}
    </LessonCurtainContext.Provider>
  );
}

function CurtainOverlay({
  curtain,
  phase,
  onEnd,
}: {
  curtain: Curtain;
  phase: "cover" | "reveal";
  onEnd: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { originX, originY } = curtain;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const radius = Math.hypot(
      Math.max(originX, window.innerWidth - originX),
      Math.max(originY, window.innerHeight - originY),
    );
    const clip = `circle(${phase === "cover" ? radius : 0}px at ${originX}px ${originY}px)`;
    /* マウント直後に値を書き換えて transition を走らせる */
    requestAnimationFrame(() => {
      el.style.clipPath = clip;
    });
  }, [originX, originY, phase]);

  return (
    <div
      ref={ref}
      className="motion-curtain"
      onTransitionEnd={(e) => e.propertyName === "clip-path" && onEnd()}
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        clipPath: `circle(0px at ${originX}px ${originY}px)`,
        transition: `clip-path ${phase === "cover" ? COVER_MS : REVEAL_MS}ms ease-in-out`,
        /* 幕で覆われている間は下の「次へ」を二度押しできないように、
           クリックはここで受け止める */
        pointerEvents: "auto",
      }}
    />
  );
}
